import { CHANNEL_TIMED_METRICS, type AlertMetric } from "@/contracts";

import i18n from "@/i18n";

export const ALERT_METRIC_LABELS: Record<AlertMetric, string> = {
  get TEMPERATURE() {
    return i18n.t("pages:alertMetric.TEMPERATURE");
  },
  get HUMIDITY() {
    return i18n.t("pages:alertMetric.HUMIDITY");
  },
  get BATTERY() {
    return i18n.t("pages:alertMetric.BATTERY");
  },
  get CONNECTIVITY() {
    return i18n.t("pages:alertMetric.CONNECTIVITY");
  },
  get DISTANCE() {
    return i18n.t("pages:alertMetric.DISTANCE");
  },
  get PRESENCE() {
    return i18n.t("pages:alertMetric.PRESENCE");
  },
  get LEAK() {
    return i18n.t("pages:alertMetric.LEAK");
  },
  get DOOR() {
    return i18n.t("pages:alertMetric.DOOR");
  },
  get CHANNEL_RANGE() {
    return i18n.t("pages:alertMetric.CHANNEL_RANGE");
  },
  get CHANNEL_ACTIVE() {
    return i18n.t("pages:alertMetric.CHANNEL_ACTIVE");
  },
  get CURRENT() {
    return i18n.t("pages:alertMetric.CURRENT");
  },
};

/** Controller channel rules carry recipients only; their timers live on each channel. */
export function metricHasRuleTimers(metric: AlertMetric): boolean {
  return !(CHANNEL_TIMED_METRICS as readonly AlertMetric[]).includes(metric);
}

/** The unit a rule's bounds are expressed in, or none for connectivity. */
export const ALERT_METRIC_UNITS: Record<AlertMetric, string> = {
  TEMPERATURE: "°C",
  HUMIDITY: "%",
  BATTERY: "%",
  CONNECTIVITY: "",
  DISTANCE: "mm",
  // A bit has no unit, and this rule carries no bounds to put one after.
  PRESENCE: "",
  LEAK: "",
  DOOR: "",
  CHANNEL_RANGE: "",
  CHANNEL_ACTIVE: "",
  CURRENT: "A",
};

/**
 * A rule's own bounds, or a note that it defers to the asset.
 *
 * Both being null is not an unconfigured rule: it means the operative range
 * comes from the asset's target range, which is the normal case (ADR-048).
 */
export function formatBounds(metric: AlertMetric, min: number | null, max: number | null): string {
  if (metric === "CONNECTIVITY") {
    return i18n.t("pages:alertBounds.silence");
  }

  // Null bounds mean something different here than they do below. Presence
  // does not defer to a range on the asset, because there is no range: what
  // it defers to is the asset's expected-occupancy hours (ADR-069).
  if (metric === "PRESENCE") {
    return i18n.t("pages:alertBounds.assetHours");
  }

  if (metric === "LEAK") {
    return i18n.t("pages:alertBounds.leakBit");
  }

  if (metric === "DOOR") {
    return i18n.t("pages:alertBounds.doorBit");
  }

  // Recipient carriers for controller channels — bounds live on DeviceChannel.
  if (metric === "CHANNEL_RANGE" || metric === "CHANNEL_ACTIVE") {
    return i18n.t("pages:alertBounds.fromChannel");
  }

  if (min === null && max === null) {
    return i18n.t("pages:alertBounds.fromAsset");
  }

  const unit = ALERT_METRIC_UNITS[metric];
  const low = min === null ? "—" : `${min}\u202f${unit}`;
  const high = max === null ? "—" : `${max}\u202f${unit}`;

  return `${low} to ${high}`;
}

/**
 * Durations in the units a person would say them in.
 *
 * Whole minutes and hours only, because the seeded values are all round and
 * the alternative is "1 h 0 min" on every row.
 */
export function formatDuration(seconds: number): string {
  if (seconds === 0) {
    return i18n.t("pages:duration.immediately");
  }

  if (seconds < 60) {
    return i18n.t("pages:duration.seconds", { count: seconds });
  }

  const minutes = Math.round(seconds / 60);

  if (minutes < 60 || minutes % 60 !== 0) {
    return i18n.t("pages:duration.minutes", { count: minutes });
  }

  const hours = minutes / 60;

  return hours === 1
    ? i18n.t("pages:duration.oneHour")
    : i18n.t("pages:duration.hours", { count: hours });
}
